/**
 * AiRiskAssessmentPanel — raises (or reopens) the AI risk assessment(s) for a use case.
 *
 * Mounted from Step6Classification.tsx, not a wizard step of its own — see that file's header
 * for why this content lives next to the tier rather than on a 9th step.
 *
 * This does not invent a new risk schema. It reuses the platform's existing Risk register —
 * the same one every other risk in Nimbus lives in — via a dedicated "AI Risk Assessments"
 * register (auto-created the first time anyone raises one) built from a Field Catalog template
 * that bundles fields that already existed (likelihood, impact, inherent_risk, mitigation,
 * residual_risk, risk_response) with three added for this feature (ai_risk_category,
 * residual_risk_acceptable, ai_governance_escalation_required). That's what maps this panel onto
 * Sections 3-6 of the company's AI Risk Assessment Template without a parallel schema.
 *
 * Deliberately excludes `inherent_risk`/`residual_risk` from the fields it renders inline: those
 * are calculated by private, unexported logic living only inside RiskDetail.tsx (likelihood x
 * impact x asset-criticality, reduced by linked-control effectiveness). Duplicating that math
 * here would be exactly the kind of drift needsAutomatedDecisionDetails() (constants/aiAct.ts)
 * already exists to prevent for a different field — so this panel shows the inputs, and links out
 * to the full risk record for the computed scores and anything else (controls, documents, RACI).
 *
 * `aiRiskAssessmentIds` is the canonical pointer list — distinct from the ordinary many-to-many
 * Linked Artefacts — so raising one here does three writes: the risk itself, a bidirectional
 * artefact link (the risk shows up on Step 7 for free), and an append to the use case's list,
 * written immediately via the API rather than staged through the wizard's dirty-patch mechanism,
 * so the link survives even if the tab closes right after creation. A use case can carry more
 * than one — e.g. a bias risk and a data-leakage risk each worth their own score and mitigation.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FieldRenderer } from '../../../components/FieldRenderer';
import { useToast } from '../../../hooks/useToast';
import { describeApiError } from '../../../utils/apiError';
import {
  aiUseCaseApi,
  configApi,
  linkedArtefactsApi,
  registersApi,
  riskApi,
} from '../../../services/api';
import { requiresAiRiskAssessment } from '../../../constants/aiAct';
import type { FieldConfig } from '../../../types/models';
import type { StepProps } from './types';

const AI_RISK_REGISTER_NAME = 'AI Risk Assessments';
const AI_RISK_TEMPLATE = 'ai_risk_assessment';

/** The lean subset this panel renders inline — everything Sections 3-6 need, minus the two
 *  calculated fields (see file header for why). */
const PANEL_FIELD_IDS = [
  'risk_title',
  'description',
  'ai_risk_category',
  'likelihood',
  'impact',
  'mitigation',
  'risk_response',
  'residual_risk_acceptable',
  'ai_governance_escalation_required',
];

interface RegisterInfo {
  registerId: string;
  fieldConfigs: FieldConfig[];
}

/** Idempotent by construction: finds the register by name first, only creates it if truly
 *  absent, and applying the template a second time is a no-op server-side (it skips any field
 *  already present in the register's config). Cached with staleTime: Infinity below and shared
 *  by both the create form and every existing-risk card — safe to resolve once per session. */
async function resolveAiRiskRegister(): Promise<RegisterInfo> {
  const registers = await registersApi.getAll();
  let reg = registers.find((r: any) => r.name === AI_RISK_REGISTER_NAME);
  if (!reg) {
    reg = await registersApi.create({
      name: AI_RISK_REGISTER_NAME,
      type: 'risk',
      template: AI_RISK_TEMPLATE,
      description: 'Risk assessments raised from the AI Use Case Register (EU AI Act).',
    });
    await configApi.applyRegisterTemplate(reg.registerId, AI_RISK_TEMPLATE);
  }
  const configs: FieldConfig[] = await configApi.getFieldConfigs(reg.registerId);
  return {
    registerId: reg.registerId,
    fieldConfigs: configs
      .filter((c) => PANEL_FIELD_IDS.includes(c.fieldId))
      .sort((a, b) => PANEL_FIELD_IDS.indexOf(a.fieldId) - PANEL_FIELD_IDS.indexOf(b.fieldId)),
  };
}

const emptyValueFor = (fieldType: string): any => (fieldType === 'multiselect' ? [] : '');

const emptyValuesFor = (fieldConfigs: FieldConfig[]): Record<string, any> => {
  const values: Record<string, any> = {};
  fieldConfigs.forEach((c) => { values[c.fieldId] = emptyValueFor(c.fieldType); });
  return values;
};

const hasRequiredMissing = (fieldConfigs: FieldConfig[], values: Record<string, any>): boolean =>
  fieldConfigs.some((c) => {
    if (!c.isRequired) return false;
    const v = values[c.fieldId];
    return Array.isArray(v) ? v.length === 0 : !v;
  });

interface AiRiskAssessmentCardProps {
  riskId: string;
  registerInfo?: RegisterInfo;
  registerLoading: boolean;
  onUnlink: () => void;
}

/** One linked risk — its own query, its own edit state, independent of every other card in the
 *  list. A 404 here (the linked risk was deleted out-of-band) only takes out this one card, not
 *  the whole panel — "Remove link" drops just this id from the use case's list. */
const AiRiskAssessmentCard: React.FC<AiRiskAssessmentCardProps> = ({
  riskId,
  registerInfo,
  registerLoading,
  onUnlink,
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const {
    data: risk,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['risk', riskId],
    queryFn: () => riskApi.getOne(riskId),
    retry: false,
  });

  const [editing, setEditing] = useState(false);
  const [editValues, setEditValues] = useState<Record<string, any>>({});
  const startEditing = () => {
    setEditValues(risk?.fields || {});
    setEditing(true);
  };

  const updateMutation = useMutation({
    mutationFn: () =>
      riskApi.update(risk!.riskId, {
        fields: { ...risk!.fields, ...editValues },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risk', riskId] });
      setEditing(false);
      toast.success('AI Risk Assessment updated.');
    },
    onError: (err) => toast.error(describeApiError(err, 'Could not update the AI risk assessment.')),
  });

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !risk) {
    return (
      <div className="np-banner np-tone-warning mb-2 d-flex align-items-center justify-content-between gap-3 flex-wrap">
        <span>
          <i className="ri-error-warning-line"></i>
          Linked risk <span className="np-tabular">{riskId}</span> could not be found — it may
          have been deleted.
        </span>
        <button type="button" className="np-btn np-btn-ghost np-btn-sm" onClick={onUnlink}>
          Remove link
        </button>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="np-card mb-2">
        <div className="np-card-body">
          {registerLoading && <div className="np-banner np-tone-neutral">Loading fields…</div>}
          {registerInfo?.fieldConfigs.map((config) => (
            <FieldRenderer
              key={config.fieldId}
              config={config}
              value={editValues[config.fieldId]}
              onChange={(fieldId, value) => setEditValues((prev) => ({ ...prev, [fieldId]: value }))}
            />
          ))}
          <div className="d-flex justify-content-end gap-2 mt-3">
            <button
              type="button"
              className="np-btn np-btn-ghost np-btn-sm"
              onClick={() => setEditing(false)}
              disabled={updateMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="np-btn np-btn-primary np-btn-sm"
              onClick={() => updateMutation.mutate()}
              disabled={updateMutation.isPending || hasRequiredMissing(registerInfo?.fieldConfigs || [], editValues)}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="np-card mb-2">
      <div className="np-card-body">
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
          <div>
            <div className="fw-semibold mb-1">{risk.fields?.risk_title || risk.riskId}</div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {risk.fields?.likelihood && (
                <span className="np-chip np-tone-neutral">Likelihood: {risk.fields.likelihood}</span>
              )}
              {risk.fields?.impact && (
                <span className="np-chip np-tone-neutral">Impact: {risk.fields.impact}</span>
              )}
              {risk.fields?.residual_risk_acceptable && (
                <span
                  className={`np-chip np-tone-${
                    risk.fields.residual_risk_acceptable === 'Acceptable' ? 'success' : 'danger'
                  }`}
                >
                  Residual risk: {risk.fields.residual_risk_acceptable}
                </span>
              )}
            </div>
          </div>
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            <button type="button" className="np-btn np-btn-ghost np-btn-sm" onClick={startEditing}>
              <i className="ri-edit-line"></i>Edit
            </button>
            <Link className="np-btn np-btn-ghost np-btn-sm" to={`/risks/${risk.riskId}`}>
              <i className="ri-external-link-line"></i>View full record
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AiRiskAssessmentPanel: React.FC<StepProps> = ({ useCase, update }) => {
  const toast = useToast();
  const required = requiresAiRiskAssessment(useCase.aiActClassification || '');
  const riskIds = useCase.aiRiskAssessmentIds || [];

  // Shared by the create form and every existing-risk card — resolved once, cached for the session.
  const {
    data: registerInfo,
    isLoading: registerLoading,
    error: registerError,
  } = useQuery({
    queryKey: ['aiRiskRegister'],
    queryFn: resolveAiRiskRegister,
    staleTime: Infinity,
  });

  const [createValues, setCreateValues] = useState<Record<string, any>>({});
  useEffect(() => {
    if (!registerInfo) return;
    setCreateValues((prev) => {
      const next = { ...prev };
      registerInfo.fieldConfigs.forEach((c) => {
        if (!(c.fieldId in next)) next[c.fieldId] = emptyValueFor(c.fieldType);
      });
      return next;
    });
  }, [registerInfo]);

  // The create form is always shown when nothing is linked yet; once at least one risk exists,
  // it collapses behind "+ Add another" so the list of already-raised risks isn't crowded out by
  // a blank form by default.
  const [showCreate, setShowCreate] = useState(false);

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!registerInfo) throw new Error('The AI Risk Assessments register is not ready yet.');
      const risk = await riskApi.create({
        registerId: registerInfo.registerId,
        fields: {
          ...createValues,
          risk_title: createValues.risk_title || `AI Risk Assessment — ${useCase.name || useCase.useCaseId}`,
        },
      });
      await linkedArtefactsApi.createLinks({
        source_id: useCase.useCaseId,
        source_type: 'ai_use_case',
        source_register: '',
        target_artefacts: [{ id: risk.riskId, type: 'risk', register: registerInfo.registerId }],
      });
      const nextIds = [...riskIds, risk.riskId];
      await aiUseCaseApi.update(useCase.useCaseId, { aiRiskAssessmentIds: nextIds });
      return { risk, nextIds };
    },
    onSuccess: ({ nextIds }, _vars, _ctx) => {
      update({ aiRiskAssessmentIds: nextIds });
      setCreateValues(registerInfo ? emptyValuesFor(registerInfo.fieldConfigs) : {});
      setShowCreate(false);
      toast.success('AI Risk Assessment raised and linked to this use case.');
    },
    onError: (err) => toast.error(describeApiError(err, 'Could not raise the AI risk assessment.')),
  });

  const unlink = (riskId: string) =>
    update({ aiRiskAssessmentIds: riskIds.filter((id) => id !== riskId) });

  const showCreateForm = riskIds.length === 0 || showCreate;

  return (
    <div className="np-field">
      <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
        <span className="np-fieldset-legend m-0">AI Risk Assessment</span>
        {required ? (
          <span className="np-chip np-tone-danger">Required for high-risk</span>
        ) : (
          <span className="np-chip np-tone-neutral">Optional at this tier</span>
        )}
      </div>
      <p className="np-hint mt-0 mb-3">
        Sections 3-6 of the AI Risk Assessment Template — categories, scoring, mitigation and
        residual risk — as real Risk register entries rather than free text, so each carries the
        same score and category as every other risk in the platform. Raise more than one if
        distinct risks (bias, data leakage, …) each need their own scoring.
      </p>

      {riskIds.map((riskId) => (
        <AiRiskAssessmentCard
          key={riskId}
          riskId={riskId}
          registerInfo={registerInfo}
          registerLoading={registerLoading}
          onUnlink={() => unlink(riskId)}
        />
      ))}

      {showCreateForm && (
        <div className="np-card">
          <div className="np-card-body">
            {registerLoading && (
              <div className="np-banner np-tone-neutral">Setting up the AI Risk Assessments register…</div>
            )}
            {registerError && (
              <div className="np-banner np-tone-danger">
                <i className="ri-error-warning-line"></i>
                Could not set up the AI Risk Assessments register. Try reloading this page.
              </div>
            )}
            {registerInfo && (
              <>
                {registerInfo.fieldConfigs.map((config) => (
                  <FieldRenderer
                    key={config.fieldId}
                    config={config}
                    value={createValues[config.fieldId]}
                    onChange={(fieldId, value) =>
                      setCreateValues((prev) => ({ ...prev, [fieldId]: value }))
                    }
                  />
                ))}
                <div className="d-flex justify-content-end gap-2 mt-3">
                  {riskIds.length > 0 && (
                    <button
                      type="button"
                      className="np-btn np-btn-ghost np-btn-sm"
                      onClick={() => setShowCreate(false)}
                      disabled={createMutation.isPending}
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="button"
                    className="np-btn np-btn-primary np-btn-sm"
                    onClick={() => createMutation.mutate()}
                    disabled={
                      createMutation.isPending || hasRequiredMissing(registerInfo.fieldConfigs, createValues)
                    }
                  >
                    <i className="ri-shield-flash-line"></i>Raise AI Risk Assessment
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {!showCreateForm && (
        <button
          type="button"
          className="np-btn np-btn-ghost np-btn-sm"
          onClick={() => setShowCreate(true)}
        >
          <i className="ri-add-line"></i>Add another AI Risk Assessment
        </button>
      )}
    </div>
  );
};

export default AiRiskAssessmentPanel;
